import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * `<Panel>` — the ONE fleetGUARDIAN card idiom: flat white surface, 1px border,
 * rounded-xl (12px), NO resting shadow. Elevation is opt-in via `hoverable`.
 * Dark mode reuses the Shadcn card/border tokens so both themes stay coherent.
 */
export interface PanelProps {
  children: ReactNode;
  className?: string;
  hoverable?: boolean;
}

export function Panel({ children, className, hoverable }: PanelProps): JSX.Element {
  return (
    <div
      className={cn(
        'rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-card',
        hoverable && 'transition-shadow hover:shadow-md',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface PanelHeaderProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

export function PanelHeader({
  title,
  subtitle,
  actions,
  className,
}: PanelHeaderProps): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 px-4 pt-4 sm:px-5',
        className,
      )}
    >
      <div className="min-w-0">
        {title && (
          <h3 className="text-[15px] font-bold leading-tight text-gray-900 dark:text-foreground">
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="mt-0.5 text-[13px] text-gray-500 dark:text-muted-foreground">
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PanelBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={cn('p-4 sm:p-5', className)}>{children}</div>;
}

export function PanelFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'border-t border-gray-200 px-4 py-3 dark:border-white/10 sm:px-5',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * `<Section>` — the chart-panel idiom: a `Panel` with a lighter heading
 * (`text-base font-semibold`) than the app `<Panel>`. Used to wrap charts.
 */
export interface SectionProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}

export function Section({
  title,
  subtitle,
  actions,
  children,
  className,
  bodyClassName,
}: SectionProps): JSX.Element {
  return (
    <Panel className={cn('p-3 sm:p-4 lg:p-5', className)}>
      {(title || actions) && (
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            {title && (
              <h3 className="text-base font-semibold leading-tight text-gray-900 dark:text-foreground">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          )}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </Panel>
  );
}

export default Panel;
